import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Button from '4all-ui/components/Button';
import {
  customModalStyles,
  VARIATION_SELECTION_OPTIONS,
  VARIATION_PRICE_TYPES,
  VARIATION_MODAL_INITIAL_STATE,
  VARIATION_INITIAL_ERROR_STATE,
  VARIATION_OPTION_INITIAL_FORM_STATE,
  VARIATION_ERROR_MESSAGE,
} from '../../helpers/constants';
import Radio from '../Radio';
import VariationOptionForm from '../VariationOptionForm';
import * as styles from './style';
import withVariations from '../../containers/GlobalVariations';
import { GroupInfo } from '../../styles/customComponents';
import FormLoader from '../FormLoader';
import { isProductVariationAddon } from '../../helpers/addons';
import Helper from '../../helpers/Helper';
import Storage from '../../helpers/storage';
import { priceToInt } from '../../helpers/price';
import Input from '../Input';

class CreateVariationModal extends PureComponent {
  state = { ...VARIATION_MODAL_INITIAL_STATE };

  componentDidUpdate(prevProps) {
    const { variationPersisted, showEditVariantModal } = this.props;

    if (
      Object.keys(variationPersisted).length &&
      JSON.stringify(prevProps.variationPersisted) !== JSON.stringify(variationPersisted) &&
      showEditVariantModal
    ) {
      /* eslint-disable react/no-did-update-set-state */
      this.setState({ ...variationPersisted }, this.setRadioOptions);
      /* eslint-enable */
    }
  }

  handleTitle = e => this.setState({ name: e.target.value });

  handleSelectionOptionChange = e => {
    const { onlyOneOption, limitMinAndMax } = VARIATION_SELECTION_OPTIONS;

    this.setState({
      [e.target.name]: Number(e.target.value),
      priceTypeBlocked: Number(e.target.value) === onlyOneOption,
      minMaxDisabled: Number(e.target.value) !== limitMinAndMax,
    });

    this.resetInputs(e);
  };

  handlePriceTypeChange = e => {
    const { priceTypeBlocked } = this.state;
    if (!priceTypeBlocked) {
      this.setState({
        [e.target.name]: Number(e.target.value),
      });
    }
  };

  handleSelectionOptionMinMax = e => {
    const regex = /^[0-9\b]+$/;
    const { value, name } = e.target;

    if ((value === '' || regex.test(value)) && value.length <= 3) {
      this.setState({ [name]: Number(value) });
    }
  };

  resetInputs = e => {
    const { priceTypeBlocked, errors } = this.state;
    if (Number(e.target.value) !== VARIATION_SELECTION_OPTIONS.limitMinAndMax) {
      this.setState({
        min: '',
        max: '',
        errors: {
          ...errors,
          min: { isInvalid: false },
          max: { isInvalid: false },
        },
      });
    }

    if (priceTypeBlocked) {
      this.setState({ priceType: VARIATION_PRICE_TYPES.totalSum });
    }
  };

  getMinMaxOption = (min, max) => {
    const { onlyOneOption, noLimitation, limitMinAndMax } = VARIATION_SELECTION_OPTIONS;
    let minMaxType = min === 1 && max === 1 ? onlyOneOption : min === 0 && max === 1000;
    minMaxType = minMaxType ? noLimitation : limitMinAndMax;
    return minMaxType;
  };

  getMinMaxValue = () => {
    const { selectionOption } = this.state;
    let min = null;
    let max = null;

    switch (selectionOption) {
      case VARIATION_SELECTION_OPTIONS.onlyOneOption:
        min = 1;
        max = 1;
        break;
      case VARIATION_SELECTION_OPTIONS.noLimitation:
        min = 0;
        max = 1000;
        break;
      default:
        break;
    }

    return min !== null && max !== null ? { min, max } : null;
  };

  removeOption = index => {
    this.setState(prevState => {
      const options = [...prevState.options];
      options.splice(index, 1);
      return { options };
    });
  };

  setOptionToEdit = id => {
    this.setState(state => ({
      form: {
        ...state.options.find(option => option.id === id),
      },
      currEditingOptionId: id,
    }));
  };

  onCancelEdit = () => {
    const { errors } = this.state;
    this.setState(prevState => ({
      options: prevState.options,
      form: { ...VARIATION_OPTION_INITIAL_FORM_STATE },
      currEditingOptionId: '',
      errors: {
        ...errors,
        atLeastOneVariationOption: { isInvalid: false },
      },
    }));
  };

  handleChange = event => {
    const { target } = event;

    this.setState(state => ({
      ...state,
      [target.name]: target.value,
    }));
  };

  handleFormChange = event => {
    const { target } = event;

    this.setState(state => ({
      form: {
        ...state.form,
        [target.name]: target.value,
      },
    }));
  };

  handleFormChangePrice = (e, value) => {
    const { form } = this.state;
    this.setState({
      form: {
        ...form,
        [e.target.name]: value,
      },
    });
  };

  validateForm = () => {
    let errors = { ...VARIATION_INITIAL_ERROR_STATE };
    let errorMessage;
    const { name, title, options, min, max, selectionOption } = this.state;
    const { limitMinAndMax } = VARIATION_SELECTION_OPTIONS;

    if (!name) {
      errors = {
        ...errors,
        name: { isInvalid: true },
      };
    }

    if (!title) {
      errors = {
        ...errors,
        title: { isInvalid: true },
      };
    }

    if (!options.length) {
      errorMessage = VARIATION_ERROR_MESSAGE.atLeastOneVariationOption;
      errors = {
        ...errors,
        atLeastOneVariationOption: { isInvalid: true },
        variationOptionsErrors: {
          ...errors.variationOptionsErrors,
          name: { isInvalid: true },
        },
      };
    }

    if (!min && min !== 0 && selectionOption === limitMinAndMax) {
      errors = {
        ...errors,
        min: { isInvalid: true },
      };
    }

    if ((!max || min > max) && selectionOption === limitMinAndMax) {
      errorMessage = min > max ? VARIATION_ERROR_MESSAGE.minGreaterThanMax : max;
      errorMessage = !errorMessage ? VARIATION_ERROR_MESSAGE.maxGreaterThanZero : null;
      errors = {
        ...errors,
        max: { isInvalid: true },
      };
    }

    if (!name || !title) {
      errorMessage = VARIATION_ERROR_MESSAGE.default;
    }
    this.setState({ errorMessage, errors });

    return Object.keys(errors).every(key => !errors[key].isInvalid);
  };

  hasInvalidField = () => {
    const { errors } = this.state;
    return Object.keys(errors).some(key => errors[key].isInvalid);
  };

  validateVariationOption = () => {
    let errors = { ...VARIATION_INITIAL_ERROR_STATE };
    const { form } = this.state;
    const { name } = form;

    if (!name) {
      errors = {
        ...errors,
        variationOptionsErrors: {
          ...errors.variationOptionsErrors,
          name: {
            isInvalid: true,
          },
        },
      };
    }

    this.setState({ errors });

    return Object.keys(errors.variationOptionsErrors).every(
      key => !errors.variationOptionsErrors[key].isInvalid
    );
  };

  handleVariationOptionSubmit = e => {
    e.preventDefault();
    console.log('ok');
    
    const { currEditingOptionId } = this.state;
    const isValid = this.validateVariationOption();
    if (isValid) {
      if (currEditingOptionId) {
        this.editOption();
      } else {
        this.addNewOption();
      }
    }
  };

  editOption = () => {
    const { form, errors } = this.state;
    const editedOption = {
      ...form,
      originalPrice: priceToInt(form.originalPrice),
    };
    this.setState(state => ({
      options: state.options.map(opt =>
        opt.id === state.currEditingOptionId ? { ...editedOption } : opt
      ),
      form: { ...VARIATION_OPTION_INITIAL_FORM_STATE },
      currEditingOptionId: '',
      errors: {
        ...errors,
        atLeastOneVariationOption: { isInvalid: false },
        variationOptionsErrors: {
          ...VARIATION_INITIAL_ERROR_STATE.variationOptionsErrors,
        },
      },
    }));
  };

  addNewOption = () => {
    const { form, errors } = this.state;
    const newOption = {
      ...form,
      originalPrice: priceToInt(form.originalPrice),
      isCreated: true,
      id: this.tempOptionId(),
    };

    this.setState(prevState => ({
      options: [...prevState.options, newOption],
      form: { ...VARIATION_OPTION_INITIAL_FORM_STATE },
      currEditingOptionId: '',
      errors: {
        ...errors,
        atLeastOneVariationOption: { isInvalid: false },
        variationOptionsErrors: {
          ...VARIATION_INITIAL_ERROR_STATE.variationOptionsErrors,
        },
      },
    }));
  };

  tempOptionId = () => {
    const { options } = this.state;
    const higherId = options.length
      ? Helper.getHigherValueInArray(options.map(option => option.id))
      : 0;

    return higherId + 1;
  };

  setRadioOptions = () => {
    const { onlyOneOption, limitMinAndMax } = VARIATION_SELECTION_OPTIONS;
    const { min, max } = this.state;
    const selectionOption = this.getMinMaxOption(min, max);

    if (selectionOption !== limitMinAndMax) {
      this.setState({ min: '', max: '' });
    }

    const priceTypeBlocked = selectionOption === onlyOneOption;
    const minMaxDisabled = selectionOption !== limitMinAndMax;

    this.setState({
      selectionOption,
      priceTypeBlocked,
      minMaxDisabled,
    });
  };

  handleClose = () => {
    const { closeModal } = this.props;
    this.setState({ ...VARIATION_MODAL_INITIAL_STATE });
    closeModal();
  };

  handleSubmit = e => {
    e.preventDefault();
    const { onSubmit, isProductVariation, variationPersisted, closeModal } = this.props;

    const isValid = this.validateForm();
    if (isValid) {
      const data = this.formatVariationData();
      this.setState({ ...VARIATION_MODAL_INITIAL_STATE });
      if (isProductVariation || isProductVariationAddon(variationPersisted)) {
        onSubmit(data, true);
        closeModal();
      } else {
        onSubmit(data);
      }
    }
  };

  formatVariationData = () => {
    const { name, title, priceType, options } = this.state;
    const { min, max } = this.getMinMaxValue() || this.state;

    const cleanOptions = options.map(opt => {
      if (opt.isCreated) {
        delete opt.isCreated;
        delete opt.id;
      }
      return opt;
    });

    const variation = {
      name,
      title,
      type: 1,
      min,
      max,
      sortOrder: 1,
      priceType,
      merchantId: Storage.getLocalStorageMerchant().idUnity,
      options: cleanOptions,
    };

    return variation;
  };

  showFormLoader = () => {
    const { isSubmiting, isCreateVariationFinished, isEditVariationFinished } = this.props;
    return isSubmiting() || isCreateVariationFinished || isEditVariationFinished;
  };

  render() {
    const {
      showCreateVariantModal,
      showEditVariantModal,
      isSubmiting,
      finishSubmitVariation,
      getVariations,
      variationError,
    } = this.props;

    const {
      errors,
      name,
      currEditingOptionId,
      errorMessage,
      title,
      form,
      max,
      min,
      minMaxDisabled,
      options,
      priceType,
      priceTypeBlocked,
      selectionOption,
    } = this.state;

    return (
      <Fragment>
        <FormLoader
          onClose={() => {
            finishSubmitVariation();
            this.handleClose();
            getVariations();
          }}
          isSubmiting={isSubmiting()}
          showModal={this.showFormLoader()}
          finishedText="Variação salva com sucesso!"
          loadingText="Salvando Variação..."
          error={variationError}
        />
        <Modal
          ariaHideApp={false}
          isOpen={showCreateVariantModal || showEditVariantModal}
          style={{
            ...customModalStyles,
            content: {
              ...customModalStyles.content,
              width: '100%',
              maxWidth: '975px',
              height: '642px',
              minWidth: '775px',
            },
          }}
        >
          <styles.Container>
            <styles.Content left>
              <styles.ContentContainer>
                <styles.StyledFormGroup small error={errors.name.isInvalid}>
                  <label>Tipo da variação</label>
                  <Input
                    name="name"
                    placeholder="Ex: Sabor, tamanho."
                    maxLength="30"
                    onChange={this.handleChange}
                    value={name}
                  />
                </styles.StyledFormGroup>

                <styles.StyledFormGroup error={errors.title.isInvalid}>
                  <label>Título da variação</label>
                  <Input
                    name="title"
                    placeholder="Ex: Escolha sua bebida."
                    maxLength="30"
                    onChange={this.handleChange}
                    value={title}
                  />
                </styles.StyledFormGroup>
              </styles.ContentContainer>

              <styles.SelectionOption>
                <GroupInfo labelStyles={styles.labelStyles} descStyles={styles.descStyles}>
                  <label>Opção de Seleção</label>
                  <p>
                    Defina a quantidade de itens da variação que podem ser adicionados ao pedido.
                  </p>
                </GroupInfo>

                <styles.RadioSelectionContainer>
                  <Radio
                    id="inp-selection-option-0"
                    label="Limitar a apenas uma opção"
                    name="selectionOption"
                    checked={selectionOption === VARIATION_SELECTION_OPTIONS.onlyOneOption}
                    value={VARIATION_SELECTION_OPTIONS.onlyOneOption}
                    onChange={this.handleSelectionOptionChange}
                    customDescStyles={styles.radioCustomDescStyles}
                  />

                  <Radio
                    id="inp-selection-option-1"
                    label="Não limitar"
                    desc="(podendo ser 0 opções)"
                    name="selectionOption"
                    checked={selectionOption === VARIATION_SELECTION_OPTIONS.noLimitation}
                    value={VARIATION_SELECTION_OPTIONS.noLimitation}
                    onChange={this.handleSelectionOptionChange}
                    customDescStyles={styles.radioCustomDescStyles}
                  />

                  <styles.MinMaxContainer>
                    <Radio
                      id="inp-selection-option-2"
                      name="selectionOption"
                      checked={selectionOption === VARIATION_SELECTION_OPTIONS.limitMinAndMax}
                      value={VARIATION_SELECTION_OPTIONS.limitMinAndMax}
                      onChange={this.handleSelectionOptionChange}
                      customDescStyles={styles.radioCustomDescStyles}
                    />
                    <styles.MinMaxBar>
                      <styles.StyledFormGroup error={errors.max.isInvalid || errors.min.isInvalid}>
                        Limitar à no mínimo
                        <styles.StyledInput
                          name="min"
                          onChange={this.handleSelectionOptionMinMax}
                          value={min}
                          disabled={minMaxDisabled}
                        />
                        e no máximo
                        <styles.StyledInput
                          name="max"
                          onChange={this.handleSelectionOptionMinMax}
                          value={max}
                          disabled={minMaxDisabled}
                        />
                        itens
                      </styles.StyledFormGroup>
                    </styles.MinMaxBar>
                  </styles.MinMaxContainer>
                </styles.RadioSelectionContainer>
              </styles.SelectionOption>

              <styles.PriceType blocked={priceTypeBlocked}>
                <GroupInfo labelStyles={styles.labelStyles} descStyles={styles.descStyles}>
                  <label>Cálculo dos preços</label>
                  <p>Escolha como o preço final do produto será calculado.</p>
                </GroupInfo>

                <styles.RadioPriceContainer>
                  <styles.RadioBar>
                    <Radio
                      id="inp-price-calc-0"
                      label="Soma Total"
                      name="priceType"
                      checked={priceType === VARIATION_PRICE_TYPES.totalSum}
                      value={VARIATION_PRICE_TYPES.totalSum}
                      onChange={this.handlePriceTypeChange}
                      customDescStyles={styles.radioCustomDescStyles}
                      disabled={priceTypeBlocked}
                    />
                    <styles.textInfo firstBar>
                      <span>Preço do Produto +</span>
                      <span>Preço Somado das Opções</span>
                    </styles.textInfo>
                  </styles.RadioBar>

                  <styles.RadioBar>
                    <Radio
                      id="inp-price-calc-1"
                      label="Preço Médio"
                      name="priceType"
                      checked={priceType === VARIATION_PRICE_TYPES.averagePrice}
                      value={VARIATION_PRICE_TYPES.averagePrice}
                      onChange={this.handlePriceTypeChange}
                      customDescStyles={styles.radioCustomDescStyles}
                      disabled={priceTypeBlocked}
                    />
                    <styles.textInfo>
                      <span>Preço do Produto +</span>
                      <span>Preço Médio das Opções</span>
                    </styles.textInfo>
                  </styles.RadioBar>
                </styles.RadioPriceContainer>
              </styles.PriceType>
            </styles.Content>

            <styles.Content>
              <styles.VariationOptionFormContent>
                <VariationOptionForm
                  options={options}
                  form={form}
                  errors={errors.variationOptionsErrors}
                  atLeastOneError={this.hasInvalidField()}
                  currentEditingId={currEditingOptionId}
                  onEdit={this.setOptionToEdit}
                  onCancelEdit={this.onCancelEdit}
                  onRemove={this.removeOption}
                  onChange={this.handleFormChange}
                  onPriceChange={this.handleFormChangePrice}
                  onSubmit={this.handleVariationOptionSubmit}
                />
              </styles.VariationOptionFormContent>

              {this.hasInvalidField() && (
                <styles.ErrorMessage>
                  <styles.ErrorIcon />
                  {errorMessage}
                </styles.ErrorMessage>
              )}

              <styles.Footer>
                <Button
                  className="close-variation-modal"
                  secondary
                  height="40px"
                  customStyles={{ lineHeight: '2px' }}
                  onClick={this.handleClose}
                >
                  Voltar
                </Button>

                <Button
                  className="save-variation-modal"
                  disabled={!!currEditingOptionId}
                  height="40px"
                  width="160px"
                  customStyles={{ lineHeight: '2px' }}
                  onClick={this.handleSubmit}
                >
                  {showEditVariantModal ? 'Editar Variação' : 'Cadastrar Variação'}
                </Button>
              </styles.Footer>
            </styles.Content>
          </styles.Container>
        </Modal>
      </Fragment>
    );
  }
}

CreateVariationModal.propTypes = {
  getVariations: PropTypes.func.isRequired,
  showCreateVariantModal: PropTypes.bool,
  showEditVariantModal: PropTypes.bool,
  closeModal: PropTypes.func.isRequired,
  isSubmiting: PropTypes.func.isRequired,
  finishSubmitVariation: PropTypes.func.isRequired,
  isCreateVariationFinished: PropTypes.bool.isRequired,
  isEditVariationFinished: PropTypes.bool.isRequired,
  variationPersisted: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isProductVariation: PropTypes.bool,
  variationError: PropTypes.object,
};

CreateVariationModal.defaultProps = {
  showCreateVariantModal: false,
  showEditVariantModal: false,
  isProductVariation: false,
  variationError: {},
};

export default withVariations(CreateVariationModal);



// WEBPACK FOOTER //
// ./src/components/CreateVariationModal/index.js